import { store } from 'modules/Editor/store';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { useCallback } from 'react';
import { WidgetId } from 'types/idTypes';
import { WidgetPosition } from 'types/widget.types';
import { LineWidgetData, Rect } from 'widgets/Lines/LineWidget/LineWidget.types';
import { groupIdCache } from 'widgets/sdk';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { calculateNewLineWidgetPos } from '../FlowCore.helpers';
import { useGetWidgetDataRect } from '../components/PortStepsGrid.hooks';

/**
 * Hook to calculate new flow line widget data based on updated widget data
 * Returns function to return all line widget updates based on updated widget data
 *
 * TODO - move this to helper file instead of hook when useGetWidgetDataRect() is refactored
 * @returns {
 *  calculateAllLineWidgetDatas: (updatedWidgets: { widgetId: WidgetId; newWidgetPos: WidgetPosition }[]) => UpdateWidget[]
 * }
 */
export const useCalculateNewLineWidgetData = () => {
  const { getRectOfGroupFromWidgetIdAsRect } = useGetWidgetDataRect();

  // Calculates all line widget datas for multiple widget updates (i.e. via multiselect toolbar)
  const calculateAllLineWidgetDatas = useCallback(
    (
      updatedWidgets: {
        widgetId: WidgetId;
        newWidgetPos: WidgetPosition;
      }[],
    ) => {
      const widgets = selectWidgets(store.getState());

      // Map of line widget id to updated line widget data
      let updatedLineDatasMap: { [key: WidgetId]: UpdateWidget } = {};
      for (const updatedWidget of updatedWidgets) {
        const { widgetId } = updatedWidget;
        const groupId = groupIdCache.getParentIdNested(widgetId);
        const connectedWidgetId = groupId || widgetId;
        const attachedLines = lineConnectionCache.getAttachedLines(connectedWidgetId);
        if (!attachedLines || !attachedLines.length) continue;

        // If group widget, calculate old and new group widget position
        let newWidgetPos = updatedWidget.newWidgetPos;
        const { leftPx, topPx, widthPx, heightPx, rotateDeg } = newWidgetPos;
        let widgetRotateDeg = newWidgetPos.rotateDeg;
        if (groupId) {
          const oldGroupDimensions = getRectOfGroupFromWidgetIdAsRect(groupId);
          const newGroupDimensions = getRectOfGroupFromWidgetIdAsRect(groupId, {
            [widgetId]: {
              x: leftPx,
              y: topPx,
              width: widthPx,
              height: heightPx,
              rotateDeg,
            },
          });
          if (!oldGroupDimensions || !newGroupDimensions) continue;

          // If the group dimensions will change with the update, then update the newWidgetDimension to be the new group dimensions
          // Otherwise, no update required
          const isDimensionChange = Object.keys(newGroupDimensions).some((key: string) => {
            const typedKey = key as keyof Rect;
            return newGroupDimensions?.[typedKey] !== oldGroupDimensions?.[typedKey];
          });
          if (!isDimensionChange) continue;

          // Update the newWidgetDimension to be the new group dimensions for calculating the new line pos
          newWidgetPos.topPx = newGroupDimensions.y;
          newWidgetPos.leftPx = newGroupDimensions.x;
          newWidgetPos.widthPx = newGroupDimensions.width;
          newWidgetPos.heightPx = newGroupDimensions.height;
          widgetRotateDeg = 0;
        }

        // Calculate new line widget position
        // Take into consideration any changes in line widget data from previous widget updates
        attachedLines.forEach((lineWidgetId) => {
          const lineWidgetData = widgets[lineWidgetId] as LineWidgetData;
          if (!lineWidgetData) return;
          const updateData = calculateNewLineWidgetPos(
            connectedWidgetId,
            { ...newWidgetPos, rotateDeg: widgetRotateDeg },
            {
              ...lineWidgetData,
              ...(updatedLineDatasMap[lineWidgetId]?.widgetData as LineWidgetData),
            },
          );

          // Set the updated line widget data in the map
          // Note: only need the latest data in case of multiple updates for a line
          // (i.e. start + end port are updated)
          if (updateData) {
            updatedLineDatasMap[lineWidgetId] = {
              widgetId: lineWidgetId,
              widgetData: updateData,
            };
          }
        });
      }

      return Object.values(updatedLineDatasMap);
    },
    [getRectOfGroupFromWidgetIdAsRect],
  );

  return {
    calculateAllLineWidgetDatas,
  };
};
